import { useMemo, useState } from 'react';
import {
  fetchLabels,
  INSTANCE_PAGE_SIZE,
  useClasses,
  useInstancesOf,
  usePredicates,
  useSubjectTriples,
  type ClassRow,
  type InstanceRow,
  type PredicateRow,
  type SubjectTriple,
} from '@/lib/useDataset';
import { GraphMultiPicker } from '@/components/GraphMultiPicker';
import { useGraphScope } from '@/lib/useGraphScope';
import { SubTabs } from '@/components/SubTabs';
import { cn } from '@/lib/utils';
import { useApiSession } from '@/lib/useApiSession';
import { isQros, useMe } from '@/lib/useMe';
import { isSelectResult, runQuery, triggerDownload } from '@/lib/api';
import { instanceStatementsQuery } from '@/lib/queryText';
import { instanceTable, instanceTableTerms, toDelimited } from '@/lib/tableExport';
import { useShownRows } from '@/lib/useShownRows';
import { ShowMoreButton } from '@/components/ShowMoreButton';
import { Button } from '@/components/ui/Button';
import { Message } from '@/components/ui/Message';
import { Pane } from "@/components/ui/Pane";

type Tab = 'classes' | 'predicates';

const GRAPHS_STORAGE_KEY = 'graphMultiPicker.browse';
const SHOW_LABELS_STORAGE_KEY = 'browse.showLabels';

/** Prefer the resolved label, fall back to the shortened URI.
 *
 *  The fallback is not cosmetic: plenty of real terms carry no label, and a
 *  blank cell where a name should be reads as missing data rather than as a
 *  term that was never named. Ported from the desktop's `resolve_display`
 *  (`ui/query_format.rs`), which makes the same choice. */
function displayTerm(uri: string, label: string, showLabels: boolean): string {
  return showLabels && label ? label : shortenUri(uri);
}

export function BrowsePage() {
  const [tab, setTab] = useState<Tab>('classes');
  // Labels on by default: the reader this page is for thinks in names, not
  // IRIs. The toggle is what makes it honest — an ontologist checking whether
  // two identically-named terms are actually the same one needs the URI, and
  // hiding it behind nothing would be worse than never showing labels at all.
  const [showLabels, setShowLabels] = useState<boolean>(() => {
    const stored = localStorage.getItem(SHOW_LABELS_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });

  const toggleLabels = (next: boolean) => {
    setShowLabels(next);
    localStorage.setItem(SHOW_LABELS_STORAGE_KEY, String(next));
  };
  // Read scope. Cold-starts on the ontology graph — the single-select this
  // replaced defaulted there — and every read below fans out over the set.
  const [graphs, setGraphs] = useGraphScope(GRAPHS_STORAGE_KEY, ['urn:tres:ontology']);
  // Drill-down state — set when a row is clicked.
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);

  const handleClassClick = (uri: string) => {
    setSelectedClass(uri);
    setSelectedSubject(null);
  };

  const handleInstanceClick = (uri: string) => {
    setSelectedSubject(uri);
  };

  return (
    <div className="container mx-auto py-8">
      <SubTabs
        tabs={[
          { to: '/query', label: 'Query' },
          { to: '/browse', label: 'Browse', page: 'browse' },
          { to: '/snippets', label: 'Snippets', page: 'snippets' },
        ]}
      />
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">Browse</h1>
        <GraphMultiPicker selected={graphs} onChange={setGraphs} />
      </div>

      {graphs.length === 0 && (
        <p className="mt-3 text-sm text-muted-foreground">
          No graph picked, so there is nothing to browse. Pick{' '}
          <span className="font-medium">All graphs</span> or any set of named graphs above.
        </p>
      )}

      <div className="mt-6 flex items-center justify-between border-b border-border">
        <div className="flex gap-2">
          <TabButton active={tab === 'classes'} onClick={() => setTab('classes')}>
            Classes
          </TabButton>
          <TabButton active={tab === 'predicates'} onClick={() => setTab('predicates')}>
            Predicates
          </TabButton>
        </div>
        <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showLabels}
            onChange={(e) => toggleLabels(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          Show labels
        </label>
      </div>

      {tab === 'classes' && (
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <ClassesPanel
            graphs={graphs}
            selected={selectedClass}
            onSelect={handleClassClick}
            showLabels={showLabels}
          />
          <InstancesPanel
            classUri={selectedClass}
            graphs={graphs}
            selected={selectedSubject}
            onSelect={handleInstanceClick}
            showLabels={showLabels}
          />
          <SubjectPanel subject={selectedSubject} graphs={graphs} />
        </div>
      )}
      {tab === 'predicates' && (
        <div className="mt-6">
          <PredicatesPanel graphs={graphs} showLabels={showLabels} />
        </div>
      )}
    </div>
  );
}

// ---- Classes column ----

function ClassesPanel({
  graphs,
  selected,
  onSelect,
  showLabels,
}: {
  graphs: string[];
  selected: string | null;
  onSelect: (uri: string) => void;
  showLabels: boolean;
}) {
  const classes = useClasses(graphs);
  const [filter, setFilter] = useState('');
  const rows = useMemo(
    () => filterRows(classes.data ?? [], filter, (c) => [c.label, c.uri]),
    [classes.data, filter],
  );

  return (
    <Pane title="Classes" loading={classes.isLoading} error={classes.error?.message ?? null}>
      <FilterBox value={filter} onChange={setFilter} what="classes" />
      <List
        rows={rows}
        empty={filter ? 'No classes match the filter.' : 'No classes in the selected graphs.'}
        renderRow={(c: ClassRow) => (
          <ListItem
            key={c.uri}
            primary={displayTerm(c.uri, c.label, showLabels)}
            secondary={`${c.instances.toLocaleString()} instance${c.instances === 1 ? '' : 's'}`}
            tooltip={c.uri}
            active={c.uri === selected}
            onClick={() => onSelect(c.uri)}
          />
        )}
      />
    </Pane>
  );
}

// ---- Instances column ----

function InstancesPanel({
  classUri,
  graphs,
  selected,
  onSelect,
  showLabels,
}: {
  classUri: string | null;
  graphs: string[];
  selected: string | null;
  onSelect: (uri: string) => void;
  showLabels: boolean;
}) {
  // The pages read are kept for one class and scope; a new pick starts at one.
  const scopeKey = `${classUri ?? ''} ${graphs.join(' ')}`;
  const [paging, setPaging] = useState({ scopeKey, pages: 1 });
  const pages = paging.scopeKey === scopeKey ? paging.pages : 1;
  const instances = useInstancesOf(classUri, graphs, pages);
  const rows = instances.data ?? [];

  if (!classUri) {
    return (
      <Pane title="Instances">
        <div className="text-sm text-muted-foreground">Select a class to see instances.</div>
      </Pane>
    );
  }

  return (
    <Pane
      title="Instances"
      subtitle={shortenUri(classUri)}
      loading={instances.isLoading}
      error={instances.error?.message ?? null}
    >
      <InstanceDownload key={classUri} classUri={classUri} graphs={graphs} />
      {rows.length > 0 && (
        <div className="mb-1 text-xs text-tres-text-secondary">
          {rows.length < instances.total
            ? `Showing ${rows.length.toLocaleString()} of ${instances.total.toLocaleString()} instances.`
            : `${instances.total.toLocaleString()} instance${instances.total === 1 ? '' : 's'}.`}
        </div>
      )}
      <List
        rows={rows}
        more={{
          hidden: instances.hasMore ? Math.max(0, instances.total - rows.length) : 0,
          what: 'instances',
          loading: instances.loadingMore,
          onClick: () => setPaging({ scopeKey, pages: pages + 1 }),
        }}
        empty="No instances."
        renderRow={(i: InstanceRow) => (
          <ListItem
            key={i.uri}
            primary={displayTerm(i.uri, i.label, showLabels)}
            tooltip={i.uri}
            active={i.uri === selected}
            onClick={() => onSelect(i.uri)}
          />
        )}
      />
    </Pane>
  );
}

type DownloadState =
  | { status: 'idle' }
  | { status: 'working' }
  | { status: 'done'; message: string }
  | { status: 'failed'; message: string };

/** Every instance of the class in the picked graphs, one row each, with a
 *  column per property, as a CSV file a spreadsheet opens. Written by the
 *  page, so nobody writes a query to take a class away as a table. */
function InstanceDownload({ classUri, graphs }: { classUri: string; graphs: string[] }) {
  const { token } = useApiSession();
  const qros = isQros(useMe().data);
  const [state, setState] = useState<DownloadState>({ status: 'idle' });

  const download = async () => {
    setState({ status: 'working' });
    const r = await runQuery(
      { query: instanceStatementsQuery(classUri, qros), ...(graphs.length > 0 ? { graphs } : {}) },
      token,
    );
    if (!r.ok) {
      setState({
        status: 'failed',
        message: `The table was not downloaded: ${r.detail ? `${r.error}: ${r.detail}` : r.error}`,
      });
      return;
    }
    if (!isSelectResult(r.data)) {
      setState({ status: 'failed', message: 'The table was not downloaded: the server sent no rows.' });
      return;
    }
    const bindings = r.data.results.bindings;
    const labels = await fetchLabels(instanceTableTerms(bindings), token);
    if (!labels.ok) {
      setState({ status: 'failed', message: `The table was not downloaded: ${labels.message}` });
      return;
    }
    const table = instanceTable(bindings, labels.labels);
    const name = classUri.slice(Math.max(classUri.lastIndexOf('#'), classUri.lastIndexOf('/'), classUri.lastIndexOf(':')) + 1) || 'class';
    triggerDownload(toDelimited(table.header, table.rows), `${name}-instances.csv`, 'text/csv;charset=utf-8');
    setState({
      status: 'done',
      message: `Downloaded ${table.rows.length.toLocaleString()} instance${table.rows.length === 1 ? '' : 's'} with ${
        table.header.length - 1
      } propert${table.header.length - 1 === 1 ? 'y' : 'ies'}.`,
    });
  };

  return (
    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => void download()}
        disabled={state.status === 'working' || graphs.length === 0}
        title="Download every instance of this class in the picked graphs as a CSV file, one row each, with a column for each property"
      >
        {state.status === 'working' ? 'Preparing the table…' : 'Download as CSV'}
      </Button>
      {state.status === 'done' && <span className="text-tres-text-secondary">{state.message}</span>}
      {state.status === 'failed' && (
        <Message kind="warning" density="compact">
          {state.message}
        </Message>
      )}
    </div>
  );
}

// ---- Subject details column ----

function SubjectPanel({ subject, graphs }: { subject: string | null; graphs: string[] }) {
  const triples = useSubjectTriples(subject, graphs);
  // Only worth showing which graph a statement came from when more than one
  // was read.
  const showGraph = graphs.length > 1;

  if (!subject) {
    return (
      <Pane title="Triples">
        <div className="text-sm text-muted-foreground">Select an instance to see triples.</div>
      </Pane>
    );
  }

  return (
    <Pane
      title="Triples"
      subtitle={shortenUri(subject)}
      loading={triples.isLoading}
      error={triples.error?.message ?? null}
    >
      <div className="space-y-2 text-xs">
        {(triples.data ?? []).map((t: SubjectTriple, i: number) => {
          const inferred = t.derivedByRule !== null;
          return (
            <div
              key={i}
              className={cn(
                'rounded border p-2',
                inferred
                  ? 'border-border border-l-[3px] border-l-tres-action bg-tres-action/5'
                  : 'border-border bg-background',
              )}
            >
              <div className="flex items-center gap-2">
                <div className="font-medium" title={t.predicate}>
                  {shortenUri(t.predicate)}
                </div>
                {inferred && (
                  <span
                    className="rounded-full border border-tres-action/40 bg-tres-action/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tres-action"
                    title={`Derived by rule: ${t.derivedByRule}`}
                  >
                    Inferred
                  </span>
                )}
                {showGraph && (
                  <span className="ml-auto truncate text-[10px] text-muted-foreground" title={t.graph}>
                    {shortenUri(t.graph)}
                  </span>
                )}
              </div>
              <div className="mt-0.5 break-all font-mono text-[11px] text-muted-foreground">
                {renderObject(t)}
              </div>
              {inferred && (
                <div className="mt-1 text-[10px] text-tres-action/80">
                  derived by rule “{t.derivedByRule}”
                </div>
              )}
            </div>
          );
        })}
        {(triples.data?.length ?? 0) === 0 && (
          <div className="text-sm text-muted-foreground">No triples about this subject.</div>
        )}
      </div>
    </Pane>
  );
}

function renderObject(t: SubjectTriple): string {
  if (t.objectType === 'uri') return `→ ${t.object}`;
  if (t.objectLang) return `"${t.object}"@${t.objectLang}`;
  if (t.objectDatatype) return `"${t.object}"^^${shortenUri(t.objectDatatype)}`;
  return `"${t.object}"`;
}

// ---- Predicates tab ----

function PredicatesPanel({ graphs, showLabels }: { graphs: string[]; showLabels: boolean }) {
  const predicates = usePredicates(graphs);
  const [filter, setFilter] = useState('');
  const rows = useMemo(
    () => filterRows(predicates.data ?? [], filter, (p) => [p.label, p.uri]),
    [predicates.data, filter],
  );
  const shownPredicates = useShownRows(rows);

  return (
    <Pane
      title={`Predicates${predicates.data ? ` (${rows.length}${filter ? ` of ${predicates.data.length}` : ''})` : ''}`}
      loading={predicates.isLoading}
      error={predicates.error?.message ?? null}
    >
      <FilterBox value={filter} onChange={setFilter} what="predicates" />
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="min-w-full text-sm">
          <thead className="border-b border-border bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Predicate</th>
              <th className="px-3 py-2 text-right font-medium">Uses</th>
            </tr>
          </thead>
          <tbody>
            {shownPredicates.shown.map((p: PredicateRow) => (
              <tr key={p.uri} className="border-b border-border last:border-0">
                <td
                  className={cn('px-3 py-2 text-xs', !(showLabels && p.label) && 'font-mono')}
                  title={p.uri}
                >
                  {displayTerm(p.uri, p.label, showLabels)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                  {p.uses.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ShowMoreButton hidden={shownPredicates.hidden} what="predicates" onClick={shownPredicates.showMore} />
        {(predicates.data?.length ?? 0) === 0 && !predicates.isLoading && (
          <div className="border-t border-border px-3 py-3 text-sm text-muted-foreground">
            No predicates in the selected graphs.
          </div>
        )}
      </div>
    </Pane>
  );
}

// ---- Reusable bits ----

// The filter the lists lost (issue #104): a text box over the loaded list,
// matching the label and the term's own spelling, client-side — the lists are
// precomputed aggregates, so nothing is re-fetched.
function filterRows<T>(rows: readonly T[], filter: string, keys: (row: T) => (string | null | undefined)[]): T[] {
  const needle = filter.trim().toLowerCase();
  if (!needle) return [...rows];
  return rows.filter((row) => keys(row).some((k) => (k ?? '').toLowerCase().includes(needle)));
}

function FilterBox({ value, onChange, what }: { value: string; onChange: (v: string) => void; what: string }) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={`Filter ${what}…`}
      aria-label={`Filter ${what}`}
      className="mb-2 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
    />
  );
}

/** The server's next page of a list read a page at a time: the list then
 *  draws every row it has and this press reads the next page. */
interface MoreFromServer {
  hidden: number;
  what: string;
  loading: boolean;
  onClick: () => void;
}

function List<T>({
  rows,
  more,
  empty,
  renderRow,
}: {
  rows: readonly T[];
  more?: MoreFromServer;
  empty: string;
  renderRow: (row: T) => React.ReactNode;
}) {
  const local = useShownRows(rows);
  if (rows.length === 0) {
    return <div className="text-sm text-muted-foreground">{empty}</div>;
  }
  const shown = more ? rows : local.shown;
  return (
    <ul className="max-h-[60vh] space-y-1 overflow-y-auto pr-1">
      {shown.map((row) => renderRow(row))}
      {more?.loading ? (
        <li className="px-2 py-1 text-xs text-muted-foreground">
          Reading the next {INSTANCE_PAGE_SIZE.toLocaleString()} {more.what}…
        </li>
      ) : more ? (
        <li>
          <ShowMoreButton hidden={more.hidden} what={more.what} onClick={more.onClick} />
        </li>
      ) : (
        local.hidden > 0 && (
          <li>
            <ShowMoreButton hidden={local.hidden} what="rows" onClick={local.showMore} />
          </li>
        )
      )}
    </ul>
  );
}

function ListItem({
  primary,
  secondary,
  tooltip,
  active,
  onClick,
}: {
  primary: string;
  secondary?: string;
  tooltip?: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        title={tooltip}
        className={cn(
          'block w-full rounded-md px-2 py-1.5 text-left text-sm',
          active
            ? 'bg-accent text-accent-foreground'
            : 'hover:bg-accent/60 text-foreground',
        )}
      >
        <div className="truncate font-mono text-xs">{primary}</div>
        {secondary && <div className="text-xs text-muted-foreground">{secondary}</div>}
      </button>
    </li>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-foreground text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// ---- URI shortening ----

function shortenUri(uri: string): string {
  if (!uri) return '';
  // Common namespaces.
  for (const [prefix, full] of WELL_KNOWN_PREFIXES) {
    if (uri.startsWith(full)) {
      return `${prefix}:${uri.slice(full.length)}`;
    }
  }
  // Fall back to the local name after the last '#' or '/'.
  const hashIdx = uri.lastIndexOf('#');
  const slashIdx = uri.lastIndexOf('/');
  const idx = Math.max(hashIdx, slashIdx);
  if (idx > 0 && idx < uri.length - 1) {
    return `…/${uri.slice(idx + 1)}`;
  }
  return uri;
}

const WELL_KNOWN_PREFIXES: Array<[string, string]> = [
  ['rdf', 'http://www.w3.org/1999/02/22-rdf-syntax-ns#'],
  ['rdfs', 'http://www.w3.org/2000/01/rdf-schema#'],
  ['owl', 'http://www.w3.org/2002/07/owl#'],
  ['xsd', 'http://www.w3.org/2001/XMLSchema#'],
  ['skos', 'http://www.w3.org/2004/02/skos/core#'],
  ['dc', 'http://purl.org/dc/elements/1.1/'],
  ['dct', 'http://purl.org/dc/terms/'],
  ['foaf', 'http://xmlns.com/foaf/0.1/'],
];
